import { CHAKRA_COLORS } from '@habity/agencies/eo';
import {
  NestedArea,
  NestedHome,
  NestedMunicipality,
} from '@habity/types';
import { bbox, featureCollection, simplify, type BBox } from '@turf/turf';
import {
  type Feature,
  type Polygon,
  type GeoJsonProperties,
  FeatureCollection,
} from 'geojson';

interface StaticMapUrlOpts {
  highDefinition?: boolean;
  w?: string;
  h?: string;
  colored?: boolean;
}

const getMapboxStaticMapUrlForHome = (
  home: NestedHome,
  opts: StaticMapUrlOpts = {
    highDefinition: true,
    w: '1280',
    h: '500',
    colored: true,
  }
) => {
  const { address } = home || {};
  const { longitude: lng, latitude: lat } = address || {};

  if (!lng || !lat) return undefined;

  return getMapboxStaticMapUrlFromLngLat(lng, lat, opts);
};

export const getMapboxStaticMapUrlFromLngLat = (
  lng: number | null,
  lat: number | null,
  opts: StaticMapUrlOpts = {
    highDefinition: true,
    w: '1280',
    h: '500',
    colored: true,
  }
) => {
  if (!lng || !lat) return undefined;

  const { highDefinition, w, h, colored } = opts;

  const baseUrl = 'https://api.mapbox.com/styles/v1/mapbox';
  const style = colored ? 'streets-v12' : 'light-v11';
  const zoom = 15;
  const scale = highDefinition ? '@2x' : '';
  const noLogo = 'logo=false';
  const token = `access_token=${process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN}`;

  return `${baseUrl}/${style}/static/${lng},${lat},${zoom}/${w}x${h}${scale}?${noLogo}&${token}`;
};

const getMapboxStaticMapUrlFromBbox = (
  bbox: BBox,
  opts: StaticMapUrlOpts = {
    highDefinition: true,
    w: '400',
    h: '480', // 1.2x
    colored: true,
  },
  overlay?: GeoJSON.GeoJSON
) => {
  if (!bbox) return undefined;

  const { highDefinition, w, h, colored } = opts;

  const baseUrl = 'https://api.mapbox.com/styles/v1/mapbox';
  const style = colored ? 'streets-v12' : 'light-v11';
  const scale = highDefinition ? '@2x' : '';
  const noLogo = 'logo=false';
  const token = `access_token=${process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN}`;
  const overlayGeoJSON = overlay ? `geojson(${JSON.stringify(overlay)})/` : '';
  const padding = 'padding=60';

  const [minX, minY, maxX, maxY] = bbox;
  const bboxStringified = `[${minX},${minY},${maxX},${maxY}]`;

  return `${baseUrl}/${style}/static/${overlayGeoJSON}${bboxStringified}/${w}x${h}${scale}?${padding}&${noLogo}&${token}`;
};

function createFeatureWithBorderFromPolygon(
  geometry: Polygon
): Feature<Polygon, GeoJsonProperties> {
  return {
    type: 'Feature',
    geometry,
    properties: {
      stroke: encodeURIComponent(CHAKRA_COLORS.black),
      fill: encodeURIComponent(CHAKRA_COLORS.black),
      'fill-opacity': 0.2,
    },
  } satisfies Feature<Polygon, GeoJsonProperties>;
}

// https://docs.mapbox.com/api/guides/#url-length-limits
const mapboxStaticMapsRequestCharLimit = 8192;
const featureCharLimit = mapboxStaticMapsRequestCharLimit - 1000; // Room for remaining url

const simplificationFactor = {
  low: 0.001,
  high: 0.01,
} as const;

function simplifyPolygonForStaticMapApi(
  feature: Feature<Polygon, GeoJsonProperties>
) {
  let simplifiedFeature = simplify(feature, {
    tolerance: simplificationFactor.low,
    highQuality: true,
  });

  if (JSON.stringify(simplifiedFeature).length > featureCharLimit) {
    simplifiedFeature = simplify(feature, {
      tolerance: simplificationFactor.high,
      highQuality: true,
    });
  }

  return simplifiedFeature;
}

export const getMapboxStaticMapUrlForMunicipality = (
  municipality: NestedMunicipality,
  opts: StaticMapUrlOpts & { overlay?: boolean } = {
    highDefinition: true,
    w: '400',
    h: '520', // 1.2x + padding to crop out attribution
    colored: false,
    overlay: true,
  }
) => {
  const { location } = municipality;
  if (!location) return undefined;

  // It comes as string with our current approach so parse to JSON first (if needed)
  const geom: Polygon = typeof location === 'string'
    ? JSON.parse(location)
    : location

  if (!geom.coordinates || geom.coordinates.length === 0) {
    return undefined;
  }

  const feature = createFeatureWithBorderFromPolygon(geom);
  const overlay = simplifyPolygonForStaticMapApi(feature);
  const municipalityBbox = bbox(feature);

  return getMapboxStaticMapUrlFromBbox(municipalityBbox, opts, overlay);
};

export const getMapboxStaticMapUrlForArea = (
  area: NestedArea,
  opts: StaticMapUrlOpts = {
    highDefinition: true,
    w: '400',
    h: '520', // 1.2x + padding to crop out attribution
    colored: false,
  }
) => {
  const { location, municipality_location } = area || {};

  if (!location && !municipality_location) return undefined;

  const fallbackGeometry = location ?? municipality_location;

  // It comes as string with our current approach so parse to JSON first (if needed)
  const geom =
    typeof fallbackGeometry === 'string'
      ? JSON.parse(fallbackGeometry)
      : fallbackGeometry;

  if (!geom.coordinates || geom.coordinates.length === 0) {
    return undefined;
  }

  const feature = createFeatureWithBorderFromPolygon(geom);
  const overlay = simplifyPolygonForStaticMapApi(feature);
  const areaBbox = bbox(feature);

  return getMapboxStaticMapUrlFromBbox(areaBbox, opts, overlay);
};

export const getMapboxStaticMapUrlForMultipleAreas = (
  areas: NestedArea[],
  opts: StaticMapUrlOpts = {
    highDefinition: true,
    w: '400',
    h: '520',
    colored: false,
  }
) => {
  if (!areas?.length) return undefined;
  if (areas.length === 1) return getMapboxStaticMapUrlForArea(areas[0], opts);

  const features = areas
    .map((area) => {
      const { geometry, location, municipality_location } = area || {};
      const fallbackGeometry = geometry ?? location ?? municipality_location;

      if (!fallbackGeometry) return null;

      const geom =
        typeof fallbackGeometry === 'string'
          ? JSON.parse(fallbackGeometry)
          : fallbackGeometry;

      if (!geom?.coordinates?.length) return null;

      return createFeatureWithBorderFromPolygon(geom);
    })
    .filter(f => f !== null);

  if (!features.length) return undefined;

  const combinedFeatures = featureCollection(features);

  const simplifiedFeatures = features.map((feature) =>
    simplifyPolygonForStaticMapApi(feature)
  );

  const overlay: FeatureCollection<Polygon, GeoJsonProperties> = {
    type: "FeatureCollection",
    features: simplifiedFeatures,
  };

  const combinedBbox = bbox(combinedFeatures);

  return getMapboxStaticMapUrlFromBbox(combinedBbox, opts, overlay);
};

export const getMapboxStaticMapUrlForMultipleMunicipalities = (
  municipalities: NestedMunicipality[],
  opts: StaticMapUrlOpts = {
    highDefinition: true,
    w: '400',
    h: '520',
    colored: false,
  }
) => {
  if (!municipalities?.length) return undefined;
  if (municipalities.length === 1) return getMapboxStaticMapUrlForMunicipality(municipalities[0], opts);

  const features = municipalities
    .map((municipality) => {
      const { location } = municipality || {};
      if (!location) return null;

      const geom: Polygon = typeof location === 'string'
        ? JSON.parse(location)
        : location;

      if (!geom?.coordinates?.length) return null;

      return createFeatureWithBorderFromPolygon(geom);
    })
    .filter(f => f !== null);

  if (!features.length) return undefined;

  const combinedFeatures = featureCollection(features);

  const simplifiedFeatures = features.map((feature) =>
    simplifyPolygonForStaticMapApi(feature)
  );

  const overlay: FeatureCollection<Polygon, GeoJsonProperties> = {
    type: "FeatureCollection",
    features: simplifiedFeatures,
  };

  const combinedBbox = bbox(combinedFeatures);

  return getMapboxStaticMapUrlFromBbox(combinedBbox, opts, overlay);
};
